import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import {
  initialFormFor, toFormValue,
  type VocabFieldType, type VocabField,
} from './vocabularyFormValues';

export type { VocabFieldType, VocabField } from './vocabularyFormValues';

type VocabService<T> = {
  list: () => Promise<{ success: boolean; data?: T[]; error?: string }>;
  create: (row: Partial<T>) => Promise<{ success: boolean; data?: T; error?: string }>;
  update: (id: string, patch: Partial<T>) => Promise<{ success: boolean; data?: T; error?: string }>;
  remove: (id: string) => Promise<{ success: boolean; error?: string }>;
};

type VocabularyTabProps<T> = {
  title: string;
  service: VocabService<T>;
  fields: Array<VocabField<T>>;
  /** Columns shown in the table; defaults to every field. */
  columns?: Array<keyof T & string>;
};

const isSelect = (t: VocabFieldType): t is { select: Array<{ value: string; label: string }> } =>
  typeof t === 'object' && t !== null && 'select' in t;

/** Render a stored value for the table. Arrays join, objects stringify, null shows as em-dash. */
function display(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

/** Form state is all strings; parse back to the column's shape on save. */
function parseField(type: VocabFieldType, raw: string): unknown {
  if (type === 'number') return raw === '' ? null : Number(raw);
  if (type === 'boolean') return raw === 'true';
  if (type === 'intArray') {
    return raw.split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(n => !Number.isNaN(n));
  }
  if (type === 'stringArray') return raw.split(',').map(s => s.trim()).filter(Boolean);
  if (type === 'json') {
    try { return raw.trim() === '' ? {} : JSON.parse(raw); } catch { return {}; }
  }
  if (type === 'jsonArray') {
    // Empty means [], not {} — an empty object fails the array CHECK on
    // bv_times_mapping.windows. Unparseable input is returned as-is so the
    // database rejects it with a visible error rather than the form silently
    // saving an empty list over the user's windows.
    if (raw.trim() === '') return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : raw;
    } catch { return raw; }
  }
  return raw === '' ? null : raw;
}

export function VocabularyTab<T extends { id: string }>(
  { title, service, fields, columns }: VocabularyTabProps<T>,
) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<T | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await service.list();
    if (res.success) { setRows(res.data ?? []); setError(null); }
    else setError(res.error ?? 'Failed to load');
    setLoading(false);
  }, [service]);

  useEffect(() => { void load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(initialFormFor(fields));
    setDialogOpen(true);
  };

  const openEdit = (row: T) => {
    setEditing(row);
    setForm(Object.fromEntries(
      fields.map(f => [f.key, toFormValue((row as Record<string, unknown>)[f.key])]),
    ));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const missing = fields.filter(f => f.required && !form[f.key]?.trim());
    if (missing.length) { setError(`Required: ${missing.map(f => f.label).join(', ')}`); return; }

    setSaving(true);
    const payload = Object.fromEntries(
      fields.map(f => [f.key, parseField(f.type, form[f.key] ?? '')]),
    ) as Partial<T>;

    const res = editing
      ? await service.update(editing.id, payload)
      : await service.create(payload);
    setSaving(false);

    if (!res.success) { setError(res.error ?? 'Save failed'); return; }
    setDialogOpen(false);
    setError(null);
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this entry?')) return;
    const res = await service.remove(id);
    if (!res.success) { setError(res.error ?? 'Delete failed'); return; }
    await load();
  };

  const cols = columns ?? fields.map(f => f.key);

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{title}</h3>
        <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>

      {error && <div className="text-sm text-destructive border border-destructive/30 rounded p-2">{error}</div>}

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {cols.map(c => (
                <TableHead key={c}>{fields.find(f => f.key === c)?.label ?? c}</TableHead>
              ))}
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols.length + 1} className="text-center text-muted-foreground py-6">
                  No entries yet.
                </TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <TableRow key={row.id}>
                {cols.map(c => (
                  <TableCell key={c}>{display((row as Record<string, unknown>)[c])}</TableCell>
                ))}
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => void handleDelete(row.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) setDialogOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title}` : `New ${title}`}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {fields.map(f => (
              <div key={f.key} className="space-y-1">
                <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>

                {isSelect(f.type) ? (
                  <Select
                    value={form[f.key] ?? ''}
                    onValueChange={v => setForm(s => ({ ...s, [f.key]: v }))}
                  >
                    <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {f.type.select.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === 'boolean' ? (
                  <Switch
                    checked={form[f.key] === 'true'}
                    onCheckedChange={v => setForm(s => ({ ...s, [f.key]: String(v) }))}
                  />
                ) : (
                  <Input
                    type={f.type === 'number' ? 'number' : 'text'}
                    value={form[f.key] ?? ''}
                    onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                  />
                )}

                {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
